import { useState } from 'react'
import FieldRow from './FieldRow'
import SectionCard from './SectionCard'
import { DateValueBox, DerivedValueBox, EditableValueBox, ValueUnit } from './ValueBox'
import PillToggle from './PillToggle'
import {
  TIMELINE_ACTUAL,
  TIMELINE_PROJECTED,
  formatScenarioDate,
  isActualTimeline,
  monthsBetween,
  withPlaceholderPurchaseDate,
} from './scenarioDates'
import DisclosureToggle from './DisclosureToggle'
import SalesProceedsPanel from './SalesProceedsPanel'
import { formatMoney, formatMoneyInput, formatTimelineMonths, isNetLoss } from './format'
import { HOLDING_PERIOD_MAX } from './scenarioNumbers'

// Only ARV has verbatim tooltip text in canvas-text.27.08.md ("Flip Summary" heading, Tool tips
// 2/2); no other row in this card has tooltip copy anywhere in the spec, so none is added. ARV keeps
// its icon in both blocks, as flip-core-inputs draws it.
// `**word**` = set in bold (700) in the canvas notes' running text (letter 99, 7.3.5; FlipInfoIcon.jsx).
const TOOLTIPS = {
  arv: '**After-Repair Value**: The property’s estimated market value after renovations.',
}

// Projected | Actual (letter 122 item 3; segments 74 + 55 = track 131, sales-proceeds-expanded). Stored
// values: scenarioDates.js TIMELINE_PROJECTED / TIMELINE_ACTUAL -- README.md ("Timeline: Projected | Actual").
const TIMELINE_OPTIONS = [
  { value: TIMELINE_PROJECTED, label: 'Projected', width: 74 },
  { value: TIMELINE_ACTUAL, label: 'Actual', width: 55 },
]

// scenario/onFieldChange/onToggleChange/computed: see README.md ("no save endpoint yet", "the
// computed prop contract"). SectionCard `plain`: legacy-inventory.19.09.md's render map gives Flip
// Summary no collapse key, and the design draws no badge or total -- README.md ("Flip Summary's card").
// purchaseDateDefault: ISO `created` + 14 (letter 145 item 2), shown as a value (typed colour) while
// Purchase Date is blank; the sale date beside Timeline follows it, as the compute does. Never handed up.
export default function FlipSummarySection({ scenario, computed, onFieldChange, onToggleChange, purchaseDateDefault = null }) {
  const s = scenario ?? {}
  const shown = withPlaceholderPurchaseDate(s, purchaseDateDefault)
  const actualTimeline = isActualTimeline(s)
  // Actual: months only while both dates give a span (otherwise the stored months are stale, not shown).
  const actualMonths = actualTimeline ? monthsBetween(shown.purchase_date, shown.sales_date) : null
  // D12: local component state, default collapsed, not persisted -- Flip Summary itself has no
  // collapse key (README.md, "Flip Summary's card"), and the design shows no
  // persisted state for this subsection either.
  const [salesProceedsExpanded, setSalesProceedsExpanded] = useState(false)
  const netLoss = isNetLoss(computed?.netProfit)

  return (
    // One card, two blocks (SOW 4, frame flip-core-inputs): Core Inputs holds every field typed here,
    // Flip Summary below it shows results only. `plain`: no badge, total or collapse button, as drawn.
    <SectionCard title="Core Inputs" firstRowGap={16} plain>
      <div className="flex flex-col">
        <FieldRow label="ARV" tooltip={TOOLTIPS.arv}>
          <EditableValueBox money value={String(s.arv ?? '')} onChange={(v) => onFieldChange('arv', v)} ariaLabel="ARV" />
        </FieldRow>

        <div className="mt-[8px]">
          <FieldRow label="Purchase Price">
            <EditableValueBox money
              value={String(s.purchase_price ?? '')}
              onChange={(v) => onFieldChange('purchase_price', v)}
              ariaLabel="Purchase Price"
            />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow label="Rehab Budget">
            <EditableValueBox money
              value={String(s.rehab_costs ?? '')}
              onChange={(v) => onFieldChange('rehab_costs', v)}
              ariaLabel="Rehab Budget"
            />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow label="Purchase Date">
            <DateValueBox
              value={s.purchase_date}
              onChange={(v) => onFieldChange('purchase_date', v)}
              placeholder={purchaseDateDefault ? formatScenarioDate(purchaseDateDefault) : undefined}
              placeholderDark={Boolean(purchaseDateDefault)}
              ariaLabel="Purchase Date"
            />
          </FieldRow>
        </div>

        {/* Timeline: 68 months box, 8, 1px line, 7, 88 sale-date box = 172, as drawn (3426 -> 3598). Projected:
            months typed, sale date calculated; Actual (letter 122 item 3): sale date typed, months calculated.
            The page keeps the pair consistent (scenarioDates.js withDerivedTimeline). Keyed per mode so
            no typing draft crosses the switch. */}
        <div className="mt-[8px]">
          <FieldRow
            label="Timeline"
            control={
              <PillToggle
                options={TIMELINE_OPTIONS}
                selected={actualTimeline ? TIMELINE_ACTUAL : TIMELINE_PROJECTED}
                onChange={(v) => {
                  if (v !== (actualTimeline ? TIMELINE_ACTUAL : TIMELINE_PROJECTED)) onToggleChange('sale_date', v)
                }}
                ariaLabel="Timeline mode"
              />
            }
          >
            {actualTimeline ? (
              <DerivedValueBox
                key="months-actual"
                width={68}
                // mr 1: "mo" ends where it does inside the bordered box of the Projected state.
                value={actualMonths == null ? null : <span className="mr-[1px]">{formatTimelineMonths(actualMonths)}<ValueUnit>mo</ValueUnit></span>}
                ariaLabel="Timeline (months)"
              />
            ) : (
              <EditableValueBox
                key="months-projected"
                value={formatTimelineMonths(s.holding_period)}
                onChange={(v) => onFieldChange('holding_period', v)}
                width={68}
                unit="mo"
                max={HOLDING_PERIOD_MAX}
                ariaLabel="Timeline (months)"
              />
            )}
            <span className="ml-[2px] mr-[1px] w-px h-[16px] bg-brand-fieldBdr shrink-0" aria-hidden="true" />
            {actualTimeline ? (
              <DateValueBox key="sale-actual" value={s.sales_date} onChange={(v) => onFieldChange('sales_date', v)} ariaLabel="Sale Date" />
            ) : (
              <DerivedValueBox key="sale-projected" value={formatScenarioDate(shown.sales_date) || null} ariaLabel="Sale Date" />
            )}
          </FieldRow>
        </div>

        {/* Divider 16 below Timeline, 1px #DDE5DA; Flip Summary's 32 header 12 below it, first row 12 below
            that (flip-core-inputs: 4679 -> 4695, 4696 -> 4708, 4740 -> 4752). */}
        <div className="mt-[16px] h-px bg-[#DDE5DA]" aria-hidden="true" />
        <h3 className="mt-[12px] flex items-center h-[32px] text-[17px] font-extrabold text-brand-dark">Flip Summary</h3>

        {/* The three inputs again, read-only: the same stored value in the same format as the box above. */}
        <div className="mt-[12px]">
          <FieldRow label="ARV" tooltip={TOOLTIPS.arv}>
            <DerivedValueBox value={formatMoneyInput(s.arv) || null} ariaLabel="ARV (Flip Summary)" />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow label="Purchase Price">
            <DerivedValueBox value={formatMoneyInput(s.purchase_price) || null} ariaLabel="Purchase Price (Flip Summary)" />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow label="Rehab Budget">
            <DerivedValueBox value={formatMoneyInput(s.rehab_costs) || null} ariaLabel="Rehab Budget (Flip Summary)" />
          </FieldRow>
        </div>

        {/* Overbudget row removed (canvas-text.27.08.md). The field itself is not: calcTotalExpenses
            (legacy calcAllIn), calcNetProfit and all four calcScore weights still take it as a
            parameter -- see defaults.js's own comment and README.md. */}

        <div className="mt-[8px]">
          {/* "Closing Costs", not "Total ...": Cong, letter 111, Comment Set #3 item 2 (this row only). */}
          <FieldRow label="Closing Costs">
            <DerivedValueBox value={formatMoney(computed?.closing_costs)} ariaLabel="Closing Costs (Flip Summary)" />
          </FieldRow>
        </div>

        {/* "(Flip Summary)" avoids the same collision "Selling Costs" hits:
            HoldingCostsSection draws its own "Holding Costs" heading on the same page. */}
        <div className="mt-[8px]">
          <FieldRow label="Holding Costs">
            <DerivedValueBox value={formatMoney(computed?.holding_cost)} ariaLabel="Holding Costs (Flip Summary)" />
          </FieldRow>
        </div>

        {/* Reads selling_costs, not at_closing -- the Selling Costs section's own body total reads
            at_closing; this rollup row and that section's own header total both read selling_costs
            (at_closing + marketing2), matching the design ($26,000, Marketing included) and the
            workbook (Loan 2026 Q3 Q14 =L36, "Selling Costs" = MROUND(L37+L42,5), L37 Marketing).
            See README.md ("at_closing vs selling_costs"). */}
        <div className="mt-[8px]">
          <FieldRow label="Selling Costs">
            <DerivedValueBox value={formatMoney(computed?.selling_costs)} ariaLabel="Selling Costs (Flip Summary)" />
          </FieldRow>
        </div>

        {/* Total-row treatment (spec 6): bold label, bold value, the value-width top divider --
            the same DerivedValueBox `bold`/`topDivider` pair Acquisition Costs and Selling Costs'
            own totals already use (ValueBox.jsx:23-43, SellingCostsSection.jsx:144). No icon on
            either row (canvas-text.27.08.md: "Remove tool tips for 'Total Expenses' and 'Net
            Profit' (on Flip Summary only)"), and the 40 pitch is unchanged -- no extra gap above. */}
        <div className="mt-[8px]">
          <FieldRow label="Total Expenses" weight="bold">
            <DerivedValueBox value={formatMoney(computed?.totalExpenses)} bold topDivider ariaLabel="Total Expenses" />
          </FieldRow>
        </div>

        {/* Net Profit: label in the ordinary dark, value #1F7A2E green (spec 6). A net figure that is
            strictly negative after rounding reads "Net Loss", label and value both #D85263
            (brand-statusSale) -- Figma "General Notes for Dev Team": "If Net Loss appears, use
            D85263 red color for the text and value." Same isNetLoss rule as the Metrics strip
            (format.js); the value keeps its minus sign and full figure (-$26,500). README.md ("Net
            Loss is strictly negative"). */}
        <div className="mt-[8px]">
          <FieldRow label={netLoss ? 'Net Loss' : 'Net Profit'} weight="bold" color={netLoss ? 'text-brand-statusSale' : 'text-brand-dark'}>
            <DerivedValueBox
              value={formatMoney(computed?.netProfit)}
              bold
              valueColorClass={netLoss ? 'text-brand-statusSale' : 'text-[#1F7A2E]'}
              ariaLabel={netLoss ? 'Net Loss' : 'Net Profit'}
            />
          </FieldRow>
        </div>

        {/* Sale Proceeds (Cong, letter 104 response 3 and 1.1.3: "Sales" -> "Sale", no tooltip): the last row of Flip Summary, collapsible/expandable (spec 1-4, canvas-
            text.27.08.md:203, "Relocate 'Sales Proceeds' to bottom of this section as a
            collapsible/expandable subsection"). Header value stays visible in both states and
            always equals the panel's own Estimated Sale Proceeds total (spec 5, D3) -- both read
            `computed?.estSaleProceeds`. No tooltip: the info icon is drawn in every frame but no
            copy exists anywhere in the spec for it (D5, FlipSummarySection.jsx's own standing rule
            below: no copy, no icon). */}
        <div className="mt-[8px]">
          <FieldRow
            label="Sale Proceeds"
            control={
              <DisclosureToggle
                collapsedLabel="View details"
                expandedLabel="Hide details"
                expanded={salesProceedsExpanded}
                onToggle={() => setSalesProceedsExpanded((v) => !v)}
              />
            }
          >
            <DerivedValueBox value={formatMoney(computed?.estSaleProceeds)} ariaLabel="Sale Proceeds" />
          </FieldRow>
          {salesProceedsExpanded ? (
            <SalesProceedsPanel
              scenarioType={s.scenarioType}
              scenario={s}
              computed={computed}
              onFieldChange={onFieldChange}
            />
          ) : null}
        </div>
      </div>
    </SectionCard>
  )
}
